import { useState, useRef, useEffect, useContext } from "react"
import { useNavigate } from "react-router-dom"
import { AuthContext } from "../context/AuthContext"
import { useNotificationContext } from "../context/NotificationContext"
import { UserCircle2, Bell, Menu } from "lucide-react"
import NotificationPopup from "./NotificationPopup"

const Header = ({ onMenuClick }) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)
  const dropdownRef = useRef(null)
  const navigate = useNavigate()
  const { logout } = useContext(AuthContext)
  const { unreadCount } = useNotificationContext()

  const toggleDropdown = () => setIsDropdownOpen((prev) => !prev)
  const handleProfileClick = () => {
    navigate("/profile")
    setIsDropdownOpen(false)
  }
  const handleLogoutClick = () => {
    logout()
    navigate("/signin")
  }

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsDropdownOpen(false)
        setShowNotifications(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  return (
    <header className="bg-white px-4 md:px-6 py-4 flex justify-between md:justify-end items-center relative border-b border-gray-200">

      {/* Mobile Menu Button */}
      <button
        onClick={onMenuClick}
        className="md:hidden p-2 -ml-2 text-gray-600 hover:bg-gray-100 rounded-lg"
      >
        <Menu className="w-6 h-6" />
      </button>

      <div className="flex items-center space-x-4 md:space-x-6" ref={dropdownRef}>
        {/* 🔔 Bell Icon with Badge */}
        <div className="relative">
          <button
            aria-label="Notifications"
            onClick={() => setShowNotifications((prev) => !prev)}
            className="w-10 h-10 flex items-center justify-center rounded-full text-gray-600 hover:text-blue-600 hover:bg-gray-100 transition-all focus:outline-none relative"
          >
            <Bell className="w-6 h-6" />
            {unreadCount > 0 && (
              <span className="absolute top-1.5 right-1.5 min-w-[18px] h-[18px] flex items-center justify-center text-[10px] font-bold text-white bg-red-500 rounded-full px-1 border-2 border-white shadow-sm">
                {unreadCount > 99 ? '99+' : unreadCount}
              </span>
            )}
          </button>
        </div>

        {/* 👤 Profile Icon with Dropdown */}
        <div className="relative">
          <button
            onClick={toggleDropdown}
            aria-label="User menu"
            className="w-10 h-10 flex items-center justify-center rounded-full text-gray-600 hover:text-blue-600 hover:bg-gray-100 transition-all focus:outline-none"
          >
            <UserCircle2 className="w-8 h-8" />
          </button>

          {isDropdownOpen && (
            <div className="absolute right-0 mt-2 w-48 bg-white border border-gray-100 rounded-xl shadow-lg z-50 py-1 origin-top-right ring-1 ring-black ring-opacity-5 focus:outline-none animate-in fade-in zoom-in-95 duration-200">
              <div className="px-4 py-2 border-b border-gray-50 mb-1">
                <p className="text-sm font-medium text-gray-900">Account</p>
              </div>
              <button
                onClick={handleProfileClick}
                className="block w-full text-left px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 hover:text-blue-600 transition-colors"
              >
                Profile Settings
              </button>
              <button
                onClick={handleLogoutClick}
                className="block w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-red-50 transition-colors"
              >
                Sign Out
              </button>
            </div>
          )}
        </div>

        {/* 🔔 Notification Modal */}
        {showNotifications && (
          <div className="absolute top-16 right-0 z-50">
            <NotificationPopup onClose={() => setShowNotifications(false)} />
          </div>
        )}
      </div>
    </header>
  )
}

export default Header
